/**
 * PersonFinder — the global "find a person" entry in the header. Location scope
 * only, gated on view-guests OR view-staff at the active location. Searching
 * lists persons attached to that location; picking one surfaces a result card
 * with two actions: open their profile, or (for guests, with manage-activities)
 * open their activities & services in a modal. Mirrors the NFC popover's
 * person-result surface so scan and search converge on the same destinations.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { useAuth } from '@/core/auth/useAuth';
import { canViewGuests, canViewStaffModule, canOperateActivities } from '@/core/auth/modulePermissions';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { searchPersons, usePersonProfilePath, type Person } from '@/entities/person';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/theme/components/ui/dialog';
import { Button } from '@/theme/components/ui/button';
import { Input } from '@/theme/components/ui/input';
import { Badge } from '@/theme/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/theme/components/ui/avatar';
import { LoadingState } from './LoadingState';
import { GuestServicesActivitiesModal } from '@/modules/services';
import { UserSearch, Search, ChevronLeft, CalendarClock, ArrowUpRight } from 'lucide-react';

function getInitials(name: string): string {
  return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

export function PersonFinder() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const personProfilePath = usePersonProfilePath();
  const { activeLocationId, isCompanyContext } = useActiveContext();

  const [open, setOpen] = useState(false);
  const [servicesPerson, setServicesPerson] = useState<{ id: string; name: string } | null>(null);

  const canUse =
    !isCompanyContext && !!activeLocationId &&
    (canViewGuests(user, activeLocationId) || canViewStaffModule(user, activeLocationId));

  if (!canUse) return null;

  const openServices = (p: Person) => { setServicesPerson({ id: p.id, name: p.name }); setOpen(false); };
  const openProfile = (p: Person) => {
    navigate(personProfilePath(p.id, p.currentRole === 'none' ? undefined : p.currentRole));
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9"
        onClick={() => setOpen(true)}
        aria-label={t('personFinder.open', 'Find a person')}
      >
        <UserSearch className="h-[18px] w-[18px]" />
      </Button>

      <PersonSearchDialog
        open={open}
        onOpenChange={setOpen}
        locationId={activeLocationId as string}
        onViewProfile={openProfile}
        onServices={openServices}
        canServices={(p) => p.currentRole === 'guest' && canOperateActivities(user, activeLocationId)}
      />

      <GuestServicesActivitiesModal
        open={!!servicesPerson}
        onOpenChange={(o) => { if (!o) setServicesPerson(null); }}
        personId={servicesPerson?.id}
        personName={servicesPerson?.name}
      />
    </>
  );
}

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string;
  onViewProfile: (p: Person) => void;
  onServices: (p: Person) => void;
  canServices: (p: Person) => boolean;
}

const ROLE_BADGE: Record<Person['currentRole'], string> = {
  guest: 'text-coral-700',
  staff: 'text-ocean-700',
  none: 'text-muted-foreground',
};

function PersonSearchDialog({ open, onOpenChange, locationId, onViewProfile, onServices, canServices }: DialogProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Person | null>(null);
  const debounced = useDebounce(query.trim(), 300);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['person-finder', locationId, debounced],
    queryFn: () => searchPersons({ search: debounced, locationId }),
    enabled: open && debounced.length >= 2,
  });

  const close = () => { onOpenChange(false); setQuery(''); setSelected(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {selected && (
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('common.back', 'Back')}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            {selected ? selected.name : t('personFinder.title', 'Find a person')}
          </DialogTitle>
        </DialogHeader>

        {!selected ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('personFinder.placeholder', 'Name, email or document…')}
                className="pl-8"
              />
            </div>

            {debounced.length < 2 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t('personFinder.hint', 'Type at least 2 characters.')}
              </p>
            ) : isFetching ? (
              <LoadingState />
            ) : results.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t('personFinder.noResults', 'No one matches at this location.')}
              </p>
            ) : (
              <div className="max-h-80 divide-y divide-border overflow-y-auto rounded-lg border border-border">
                {results.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelected(p)}
                    className="flex w-full items-center gap-3 p-2.5 text-left hover:bg-muted/50"
                  >
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={p.avatarUrl} alt={p.name} />
                      <AvatarFallback className="bg-gradient-to-br from-sun-500 to-coral-500 text-[11px] font-semibold text-white">
                        {getInitials(p.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{p.name}</p>
                      {p.email && <p className="truncate text-xs text-muted-foreground">{p.email}</p>}
                    </div>
                    <Badge variant="outline" className={ROLE_BADGE[p.currentRole]}>
                      {t(`personFinder.role.${p.currentRole}`, p.currentRole)}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border border-border p-3">
              <Avatar className="h-11 w-11 shrink-0">
                <AvatarImage src={selected.avatarUrl} alt={selected.name} />
                <AvatarFallback className="bg-gradient-to-br from-sun-500 to-coral-500 font-serif text-sm font-semibold text-white">
                  {getInitials(selected.name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{selected.name}</p>
                {selected.email && <p className="truncate text-xs text-muted-foreground">{selected.email}</p>}
              </div>
              <Badge variant="outline" className={ROLE_BADGE[selected.currentRole]}>
                {t(`personFinder.role.${selected.currentRole}`, selected.currentRole)}
              </Badge>
            </div>

            <div className="flex flex-col gap-2">
              {canServices(selected) && (
                <Button onClick={() => onServices(selected)} className="justify-start gap-2">
                  <CalendarClock className="h-4 w-4" />
                  {t('nfc.scan.activitiesServices', 'Activities & services')}
                </Button>
              )}
              <Button variant="outline" onClick={() => onViewProfile(selected)} className="justify-start gap-2">
                <ArrowUpRight className="h-4 w-4" />
                {t('nfc.scan.viewProfile', 'View profile')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
